import React from 'react';
import Datetime from 'react-datetime';
import { withTranslation } from 'react-i18next';
import i18n from '../../../locales/i18n';
import moment from 'moment';
import 'moment/locale/is';
moment.locale('is');

import 'rc-time-picker/assets/index.css';
import axios from 'axios';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog.js';

// @material-ui
import {
    FormControlLabel,
    FormControl,
    FormLabel,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    TextField,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import Autocomplete from '@mui/material/Autocomplete';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import { FlexContainer } from 'components/FlexContainer/FlexContainer';
import HoverInfoBox from 'components/HoverInfoBox/HoverInfoBox';
import AddCompanyFrom1819Modal from 'components/AddCompanyFrom1819Modal/AddCompanyFrom1819Modal';
import { E2E_TINY_PNG, isApiMockEnabled } from 'api/mock';
import { EVENTS_ENABLED } from 'config';
import styles from './Step1.module.scss';

// Action types that have an additional value input (label looked up via
// `offer_active_<type>` and helper text via `offer.activation_help_<type>`).
const ACTION_TYPES_WITH_VALUE = ['email', 'website', 'promocode', 'phone'];

const SHOW_MEMBERSHIP_CARD_ACTIVATION = true;
const SHOW_LIMITED_ACTIVATION_OPTIONS = true;

// Action types that have a help/tooltip text shown next to the dropdown.
const ACTION_TYPES_WITH_HELP = [
    'timer',
    'promocode',
    'email',
    'phone',
    'website',
    'barcode',
    'qrcode',
    ...(SHOW_MEMBERSHIP_CARD_ACTIVATION ? ['membership_card'] : []),
];

// "Limited" (Takmarkað) is a per-user activation-frequency restriction that can be layered
// on top of certain base action types. Each restrictable base type has a corresponding
// stored action_type value; the admin UI presents this as a Fjölnota/Einnota segment over
// the base type rather than as a separate dropdown entry.
const LIMITED_ACTION_TYPE_MAP = {
    timer: 'limited',
    membership_card: 'limited_membership_card',
};
const BASE_ACTION_TYPE_MAP = Object.fromEntries(
    Object.entries(LIMITED_ACTION_TYPE_MAP).map(([base, limited]) => [limited, base])
);
const LIMITED_ACTION_TYPES = Object.values(LIMITED_ACTION_TYPE_MAP);
const isLimitedActionType = (actionType) => LIMITED_ACTION_TYPES.includes(actionType);
// Resolves the stored action_type to the base type shown in the main dropdown
// (e.g. 'limited' -> 'timer', 'limited_membership_card' -> 'membership_card').
const toBaseActionType = (actionType) => BASE_ACTION_TYPE_MAP[actionType] || actionType;

// Maps the admin-facing activation-frequency preset to the two stored fields
// (activation_once_only / activation_frequency_days). Only used when
// action_type === 'limited'. Day counts are approximations (see requirements FR1).
const ACTIVATION_FREQUENCY_PRESET_MAP = {
    once: { activation_once_only: true, activation_frequency_days: null },
    week: { activation_once_only: false, activation_frequency_days: 7 },
    month: { activation_once_only: false, activation_frequency_days: 30 },
    quarter: { activation_once_only: false, activation_frequency_days: 90 },
    year: { activation_once_only: false, activation_frequency_days: 365 },
};

function mapActivationFrequencyPresetToFields(preset) {
    return (
        ACTIVATION_FREQUENCY_PRESET_MAP[preset] || {
            activation_once_only: false,
            activation_frequency_days: null,
        }
    );
}

// Reverse of the map above: given the stored fields, derive which preset is selected
// (used when loading an existing 'limited' offer into the edit form). Returns null when
// the stored values don't match a known preset (e.g. a future custom day count).
function deriveActivationFrequencyPreset(onceOnly, days) {
    if (onceOnly) return 'once';
    const match = Object.keys(ACTIVATION_FREQUENCY_PRESET_MAP).find(
        (key) => ACTIVATION_FREQUENCY_PRESET_MAP[key].activation_frequency_days === days
    );
    return match || null;
}

// Maps the active_day index used in torgid_offer_active_times (Mon=0..Sun=6)
// to a translation key for the short weekday label.
const DAY_SHORT_KEYS = [
    'day_short_mon',
    'day_short_tue',
    'day_short_wed',
    'day_short_thu',
    'day_short_fri',
    'day_short_sat',
    'day_short_sun',
];

class Step1 extends React.Component {
    constructor(props) {
        super(props);
        const hasPrefill = !!(props.offerData.id || props.offerData.isDuplicate);
        let extraImages =
            props.offerData.offer_images && props.offerData.offer_images.length > 0
                ? props.offerData.offer_images
                : [];
        // If props.offerData.image contains the same image as one of the extra images, remove it from the extra images
        if (props.offerData.image) {
            extraImages = extraImages.filter((x) => x.image !== props.offerData.image);
        }
        this.store = JSON.parse(localStorage.getItem('user'));
        this.user_is_group_admin = !!(this.store && this.store.roles.includes('group_admin'));
        this.user_is_company_user = !!(this.store && this.store.roles.includes('company_user'));
        this.user_is_group_id = this.store.user.torgid_special_company_id;
        this.isAdmin = !!(
            this.store &&
            this.store.permission &&
            this.store.permission.includes('offer-create')
        );

        let hasImages = false;

        if (props.offerData.offer_images && props.offerData.offer_images.length > 0) {
            hasImages = true;
        }
        if (props.offerData.image) {
            hasImages = true;
        }
        let discount = '';
        const rawDiscount = props?.offerData?.discount || '';
        if (rawDiscount.includes('%')) {
            discount = 'discount';
        } else if (rawDiscount.includes('kr.')) {
            discount = 'price';
        } else if (
            rawDiscount === '2 fyrir 1' ||
            rawDiscount === 'sérkjör' ||
            rawDiscount === 'tilkynning' ||
            rawDiscount === 'skráning'
        ) {
            discount = rawDiscount;
        }

        this.state = {
            dialogOpen: false,
            dialogMessage: null,
            torgid_user_id: JSON.parse(localStorage.getItem('user')).user.torgid_user_id,
            id: props.offerData.id || '',
            title: props.offerData.title || '',
            // Validation passes when either the Icelandic or the English
            // version is filled in (EN gets copied to IS on save).
            titleState:
                props.offerData.title || props.offerData.title_en ? 'success' : '',
            title_en: props.offerData.title_en || '',
            description: props.offerData.description || '',
            descriptionState:
                props.offerData.description || props.offerData.description_en
                    ? 'success'
                    : '',
            description_en: props.offerData.description_en || '',
            discount,
            discountState: discount ? 'success' : '',
            discountInput: !props.offerData.discount
                ? ''
                : props.offerData.discount.includes('%')
                ? props.offerData.discount.slice(0, -1)
                : props.offerData.discount.includes('kr.')
                ? props.offerData.discount.slice(0, -3)
                : '',
            // special_discount: !props.offerData.special_discount ? "" : props.offerData.special_discount.includes('%') ? props.offerData.special_discount.slice(0, -1)  : props.offerData.special_discount,
            image: props.offerData.image || '',
            images: extraImages,

            imagesState: hasImages ? 'success' : '',
            action_type:
                props.offerData.action_type === 'lead_form'
                    ? 'timer'
                    : props.offerData.action_type || 'timer',
            activation_once_only: props.offerData.activation_once_only || false,
            activation_frequency_days:
                props.offerData.activation_frequency_days != null
                    ? props.offerData.activation_frequency_days
                    : null,
            activationFrequencyPreset: isLimitedActionType(props.offerData.action_type)
                ? deriveActivationFrequencyPreset(
                      props.offerData.activation_once_only,
                      props.offerData.activation_frequency_days
                  )
                : null,
            activationFrequencyState: '',
            action_value: props.offerData.action_value || '',
            promocode: props.offerData.promocode || '',
            offer_link: props.offerData.offer_link || '',
            is_active: hasPrefill ? props.offerData.is_active : true,
            start_date: props.offerData.start_date || '',
            end_date: props.offerData.end_date || '',
            torgid_offer_active_times: [
                {
                    active_day: 0,
                    day: 'mán.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 1,
                    day: 'þri.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 2,
                    day: 'mið.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 3,
                    day: 'fim.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 4,
                    day: 'fös.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 5,
                    day: 'lau.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
                {
                    active_day: 6,
                    day: 'sun.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: hasPrefill ? false : true,
                    active24: true,
                },
            ],
            active_times_status: false,
            torgid_special_company_id: props.offerData.torgid_special_company
                ? props.offerData.torgid_special_company.id
                : this.user_is_group_admin
                ? this.user_is_group_id
                : 0,
            companyId:
                hasPrefill && props.offerData.torgid_company
                    ? props.offerData.torgid_company.id
                    : this.user_is_company_user
                    ? JSON.parse(localStorage.getItem('user')).user.torgid_company_id
                    : '',
            companyState:
                hasPrefill && props.offerData.torgid_company
                    ? 'success'
                    : this.user_is_company_user
                    ? 'success'
                    : '',
            companies: JSON.parse(localStorage.getItem('user')).companies || [],
            addCompanyModalOpen: false,
            pendingCreatedCompany: null,
        };
        this.isSuperAdmin = !!(this.store && this.store.roles?.includes('super_admin'));
        // super_admin and group_admin may create companies from the offer screen.
        // Company users edit their own companies via /company/:id.
        this.canCreateCompany = this.isSuperAdmin || this.user_is_group_admin;
    }

    componentDidMount() {
        if (this.props.offerData.torgid_offer_active_times) {
            let activeDaysState = [...this.state.torgid_offer_active_times];
            this.props.offerData.torgid_offer_active_times.forEach((value) => {
                if (value.start_time?.length === 8) {
                    // remove the seconds from the time
                    value.start_time = value.start_time.slice(0, -3);
                }
                if (value.end_time?.length === 8) {
                    // remove the seconds from the time
                    value.end_time = value.end_time.slice(0, -3);
                }
                activeDaysState[value.active_day].start_time = value.start_time;
                activeDaysState[value.active_day].end_time = value.end_time;
                activeDaysState[value.active_day].active = true;
                if (value.start_time === '00:00' && value.end_time === '23:59') {
                    activeDaysState[value.active_day].active24 = true;
                }
            });
            this.setState({ torgid_offer_active_times: activeDaysState });
        }
        if (!this.state.id && this.state.companyId && !this.user_is_group_admin) {
            this.props.getData(this.state.companyId);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState !== this.state && this.props.onStateChange) {
            this.props.onStateChange(this.state);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const hasPrefill = !!(props.offerData.id || props.offerData.isDuplicate);
        if (
            !hasPrefill &&
            state.companyId &&
            props.data2 &&
            props.data2.results &&
            state.active_times_status
        ) {
            let activeDays = [
                {
                    active_day: 0,
                    day: 'mán.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 1,
                    day: 'þri.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 2,
                    day: 'mið.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 3,
                    day: 'fim.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 4,
                    day: 'fös.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 5,
                    day: 'lau.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
                {
                    active_day: 6,
                    day: 'sun.',
                    start_time: '00:00',
                    end_time: '23:59',
                    active: true,
                    active24: true,
                },
            ];
            if (props.data2.results.torgid_company_locations.length > 0) {
                props.data2.results.torgid_company_locations[0].torgid_company_openings.forEach(
                    (value) => {
                        if (value.start_time?.length === 8) {
                            // remove the seconds from the time
                            value.start_time = value.start_time.slice(0, -3);
                        }
                        if (value.end_time?.length === 8) {
                            // remove the seconds from the time
                            value.end_time = value.end_time.slice(0, -3);
                        }

                        activeDays[value.day].start_time = value.open_time;
                        activeDays[value.day].end_time = value.close_time;
                        activeDays[value.day].active = true;

                        if (value.start_time === '00:00' && value.end_time === '23:59') {
                            activeDays[value.day].active24 = true;
                        }
                    }
                );
            }
            return { torgid_offer_active_times: activeDays, active_times_status: false };
        }
        return null;
    }

    sendState() {
        return this.state;
    }

    verifyLength(value, length) {
        return value.length >= length;
    }

    handleChange = (data, type) => {
        const update = { [type]: data };
        if (type === 'discount' && this.state.discountState === 'error') {
            update.discountState = '';
        }
        // When switching away from a 'limited*' action type, clear the activation-frequency
        // selection so a previously-chosen preset is not silently resubmitted.
        if (type === 'action_type' && !isLimitedActionType(data)) {
            update.activationFrequencyPreset = null;
            update.activation_once_only = false;
            update.activation_frequency_days = null;
            update.activationFrequencyState = '';
        }
        this.setState(update);
    };

    handleActivationFrequencyPresetChange = (preset) => {
        const { activation_once_only, activation_frequency_days } =
            mapActivationFrequencyPresetToFields(preset);
        this.setState({
            activationFrequencyPreset: preset,
            activation_once_only,
            activation_frequency_days,
            activationFrequencyState: 'success',
        });
    };

    // Segment selector shown for restrictable action types (Niðurtalning / Sýna
    // félagsskírteini): Fjölnota (multi-use) keeps the base action_type; Einnota
    // (single-use) switches to its 'limited*' counterpart, which reveals the
    // activation-frequency selector.
    handleActivationModeChange = (mode) => {
        // ToggleButtonGroup emits null when the active button is re-pressed; ignore so a
        // mode always stays selected.
        if (mode === null) return;
        const baseType = toBaseActionType(this.state.action_type);
        if (mode === 'einnota') {
            this.setState({ action_type: LIMITED_ACTION_TYPE_MAP[baseType] || baseType });
        } else {
            this.setState({
                action_type: baseType,
                activationFrequencyPreset: null,
                activation_once_only: false,
                activation_frequency_days: null,
                activationFrequencyState: '',
            });
        }
    };

    handleChangeCompanyGroup = (data, type) => {
        if (!data || data === null) {
            this.setState({
                [type]: null,
                companyState: '',
            });
            return;
        }
        if (!this.state.id && !this.user_is_group_admin) {
            this.props.getData(data.id);
        }
        this.setState({
            [type]: data.id,
            companyState: 'success',
            active_times_status: true,
        });
    };

    handleChangeCompany = (data, type) => {
        if (!this.state.id && !this.user_is_group_admin) {
            this.props.getData(data);
        }
        this.setState({
            [type]: data,
            companyState: 'success',
            active_times_status: true,
        });
    };

    handleCompanyCreatedFrom1819 = (company) => {
        if (!company?.id) return;

        if (typeof this.props.onRefreshCompanies === 'function') {
            this.props.onRefreshCompanies();
        }

        if (!this.state.id) {
            this.props.getData?.(company.id);
        }

        this.setState({
            companyId: company.id,
            companyState: 'success',
            active_times_status: true,
            addCompanyModalOpen: false,
            pendingCreatedCompany: {
                id: company.id,
                name: company.name || '',
                logo: company.logo || '',
                firstLetter: (company.name || '?')[0]?.toUpperCase?.() || '?',
            },
        });
    };

    onUpload(data, index, id) {
        let images = this.state.images;
        // If index is more than the length of the images array, then it is a new image
        if (index >= images.length) {
            images.push({
                id: null,
                image: data,
                sort_order: this.state.images.length + 2,
            });
        }

        if (
            images[index].image &&
            images[index].image !== '' &&
            images?.[index]?.image?.includes('http')
        ) {
            images[index].deleted = true;
            images.push({
                id: null,
                image: data,
                sort_order: this.state.images.length + 2,
            });
        } else {
            images[index].id = id;
            images[index].image = data;
            images[index].sort_order = index + 2;
        }

        this.setState({
            images: images,
            imagesState: 'success',
        });
    }

    changeImageOrder(currentIndex, newIndex) {
        let images = this.state.images;
        let currentItem = images[currentIndex];
        images.splice(currentIndex, 1);
        images.splice(newIndex, 0, currentItem);
        this.setState({ images: images });
    }

    deleteImageItem(index) {
        let images = [...this.state.images];
        if (images[index].id) {
            images[index].deleted = true;
            images.splice(index, 1);
        } else {
            images.splice(index, 1);
        }

        this.setState({ images: images }, () => {
            this.validateImages();
        });
    }

    addNewImage = () => {
        this.setState({
            images: [
                ...this.state.images,
                {
                    id: null,
                    image: '',
                    sort_order: this.state.images.length + 2,
                },
            ],
        });
    };

    change(event, stateName, type, stateNameEqualTo) {
        const value = event.target.value;
        if (type === 'length') {
            if (this.verifyLength(value, stateNameEqualTo)) {
                this.setState({ [stateName + 'State']: 'success' });
            } else {
                this.setState({ [stateName + 'State']: '' });
            }
        }
        const update = { [stateName]: value };

        // ── EN → IS fallback ────────────────────────────────────────────
        // When the user only fills out the English version we copy the
        // English text into the Icelandic version on save, so the
        // corresponding *State (titleState / descriptionState) should also
        // count the English field as a valid source. This lets the user
        // progress through the form without typing Icelandic text first.
        if (stateName === 'title_en') {
            const currentTitle = (this.state.title || '').trim();
            if (!currentTitle) {
                update.titleState = this.verifyLength(value, 5) ? 'success' : '';
            }
        }
        if (stateName === 'title') {
            const currentTitleEn = (this.state.title_en || '').trim();
            if (!value.trim() && currentTitleEn) {
                update.titleState = this.verifyLength(currentTitleEn, 5) ? 'success' : '';
            }
        }
        if (stateName === 'description_en') {
            const currentDescription = (this.state.description || '').trim();
            if (!currentDescription) {
                update.descriptionState = this.verifyLength(value, 10) ? 'success' : '';
            }
        }
        if (stateName === 'description') {
            const currentDescriptionEn = (this.state.description_en || '').trim();
            if (!value.trim() && currentDescriptionEn) {
                update.descriptionState = this.verifyLength(currentDescriptionEn, 10)
                    ? 'success'
                    : '';
            }
        }

        if (stateName === 'discountInput' && this.state.discountState === 'error') {
            update.discountState = '';
        }
        this.setState(update);
    }

    hideAlert = () => {
        this.setState({ dialogOpen: false, dialogMessage: null });
    };
    isValidated() {
        const { discount, discountInput } = this.state;
        const needsInput = discount === 'discount' || discount === 'price';
        const discountValid = discount && (!needsInput || (discountInput && discountInput.trim()));

        // A 'limited*' offer must have an activation-frequency preset selected.
        const activationFrequencyValid =
            !isLimitedActionType(this.state.action_type) || !!this.state.activationFrequencyPreset;

        if (
            this.state.titleState === 'success' &&
            this.state.descriptionState === 'success' &&
            this.state.companyState === 'success' &&
            this.state.imagesState === 'success' &&
            discountValid &&
            activationFrequencyValid
        ) {
            return true;
        }

        const { t } = this.props;
        const missing = [];
        let firstErrorRef = null;
        if (this.state.titleState !== 'success') {
            this.setState({ titleState: 'error' });
            missing.push(t('offer.validation_title'));
            if (!firstErrorRef) firstErrorRef = 'textSection';
        }
        if (this.state.descriptionState !== 'success') {
            this.setState({ descriptionState: 'error' });
            missing.push(t('offer.validation_description'));
            if (!firstErrorRef) firstErrorRef = 'textSection';
        }
        if (this.state.companyState !== 'success') {
            this.setState({ companyState: 'error' });
            missing.push(t('offer.validation_company'));
            if (!firstErrorRef) firstErrorRef = 'infoSection';
        }
        if (this.state.imagesState !== 'success') {
            this.setState({ imagesState: 'error' });
            missing.push(t('offer.validation_image'));
            if (!firstErrorRef) firstErrorRef = 'imagesSection';
        }
        if (!discountValid) {
            this.setState({ discountState: 'error' });
            if (!discount) {
                missing.push(t('offer.type'));
            } else {
                missing.push(t('offer_displayed_discount'));
            }
            if (!firstErrorRef) firstErrorRef = 'discountSection';
        }
        if (!activationFrequencyValid) {
            this.setState({ activationFrequencyState: 'error' });
            missing.push(t('offerActivationFrequencyRequired'));
            if (!firstErrorRef) firstErrorRef = 'activationSection';
        }

        this.setState({
            dialogOpen: true,
            dialogMessage: (
                <span>
                    {t('offer.validation_intro')}
                    <br /><br />
                    {missing.map((item, i) => (
                        <span key={i} style={{ display: 'block', paddingLeft: 8, lineHeight: 1.8 }}>• {item}</span>
                    ))}
                </span>
            ),
        });

        if (firstErrorRef && this[firstErrorRef]) {
            this[firstErrorRef].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
    }

    handleActiveTime(index) {
        let activeTimes = [...this.state.torgid_offer_active_times];
        activeTimes[index] = {
            active_day: index,
            day: activeTimes[index].day,
            start_time: activeTimes[index].start_time,
            end_time: activeTimes[index].end_time,
            active: !activeTimes[index].active,
            active24: activeTimes[index].active24,
        };
        this.setState({ torgid_offer_active_times: activeTimes });
    }

    handleActiveTimeDef(index) {
        let activeTimes = [...this.state.torgid_offer_active_times];
        activeTimes[index] = {
            active_day: index,
            day: activeTimes[index].day,
            start_time: '00:00',
            end_time: '23:59',
            active: true,
            active24: true,
        };
        this.setState({ torgid_offer_active_times: activeTimes });
    }

    handleChangeTime = (e, index, type) => {
        console.log(e, index, type);

        let val = e;
        let activeTimes = [...this.state.torgid_offer_active_times];
        activeTimes[index] = {
            active_day: index,
            day: activeTimes[index].day,
            start_time: type === 'st' ? val : activeTimes[index].start_time,
            end_time: type === 'et' ? val : activeTimes[index].end_time,
            active: true,
        };

        // check if start time and end time are 00:00 and 23:59
        if (activeTimes[index].start_time === '00:00' && activeTimes[index].end_time === '23:59') {
            activeTimes[index].active24 = true;
        } else {
            activeTimes[index].active24 = false;
        }

        this.setState({ torgid_offer_active_times: activeTimes });
    };

    autoTranslate = (state, type, fromLn, toLn) => {
        let textOld = this.state[state];
        let text = textOld.replace(/&/g, '');
        axios
            .get(
                `https://translation.googleapis.com/language/translate/v2?key=AIzaSyA5AF_VTEbn4TXQ2uMWsaEx0Al53mT7aOU&q=${text}&source=${fromLn}&target=${toLn}`
            )
            .then((response) => {
                if (response.status === 200) {
                    let translatedText = response.data.data.translations[0].translatedText;
                    this.setState({
                        [type]: translatedText.replace(new RegExp('&#39;', 'g'), "'"),
                    });
                } else {
                    this.setState({
                        dialogOpen: true,
                        dialogMessage: response.statusText,
                    });
                }
            });
    };

    handleSetAsMainImage = (index) => {
        const images = [...this.state.images];
        const oldMain = this.state.image;
        const newMain = images[index].image;

        if (oldMain) {
            // Keep the slot's existing id so the server updates that record with the old main URL
            images[index] = { ...images[index], image: oldMain };
        } else {
            images.splice(index, 1);
        }

        this.setState({ image: newMain, images }, () => {
            this.validateImages();
        });
    };

    handleDeleteMainImage = () => {
        let images = this.state.images;
        let newImage = '';
        if (images.length > 0) {
            // Remove the first element of the images array
            newImage = images[0].image;
            images.shift();
        } else {
            newImage = '';
        }
        this.setState({ image: newImage }, () => {
            this.validateImages();
        });
    };

    validateImages = () => {
        let hasImages = false;

        if (this.state.images && this.state.images.length > 0) {
            hasImages = true;
        }
        if (this.state.image) {
            hasImages = true;
        }
        this.setState({ imagesState: hasImages ? 'success' : '' });
    };

    onUploadMainImage(data) {
        this.setState({
            image: data,
            imagesState: 'success',
        });
    }

    handleSelectAllActiveTimes = (e) => {
        let activeTimes = [...this.state.torgid_offer_active_times];
        activeTimes.map((value) => {
            value.active = true;
            value.active24 = true;
            value.start_time = '00:00';
            value.end_time = '23:59';
            return value;
        });
        this.setState({ torgid_offer_active_times: activeTimes });
    };

    render() {
        const { classes, data, companies, isAdmin, t } = this.props;
        const { title, discount, discountInput, companyId } = this.state;

        const selectedCompany =
            (companyId && companies?.results?.find((c) => c.id === companyId)) ||
            (this.props.data2?.id ? this.props.data2 : null);

        let discountStr = null;
        if (discount === 'discount' && discountInput?.trim()) {
            let d = discountInput.trim();
            if (!d.startsWith('-')) d = '-' + d;
            if (!d.endsWith('%')) d = d + '%';
            discountStr = d;
        } else if (discount === 'price' && discountInput?.trim()) {
            discountStr = discountInput.replace('kr.', '').replace('kr', '').trim() + ' kr.';
        } else if (discount && discount !== 'discount' && discount !== 'price') {
            discountStr = discount;
        }

        const previewOffer = {
            title,
            discount: discountStr,
            torgid_company: selectedCompany
                ? { name: selectedCompany.name, logo: selectedCompany.logo }
                : null,
        };

        const renderActiveTime =
            this.state.torgid_offer_active_times &&
            this.state.torgid_offer_active_times.map((value, index) => (
                <div key={index} className={styles.dayRow}>
                    <button
                        className={`${styles.dayToggle} ${
                            value.active ? styles.dayToggleActive : ''
                        }`}
                        onClick={() => this.handleActiveTime(index)}
                        type="button"
                    >
                        {t(DAY_SHORT_KEYS[value.active_day] || 'day_short_mon')}
                    </button>
                    {value.active && (
                        <div className={styles.timeRange}>
                            <HourInput
                                value={this.state.torgid_offer_active_times[index].start_time}
                                onChange={(e) => this.handleChangeTime(e, index, 'st')}
                            />
                            <span className={styles.timeSep}>—</span>
                            <HourInput
                                value={this.state.torgid_offer_active_times[index].end_time}
                                onChange={(e) => this.handleChangeTime(e, index, 'et')}
                            />
                            <button
                                type="button"
                                className={`${styles.allDayBtn} ${
                                    value.active24 ? styles.allDayBtnActive : ''
                                }`}
                                onClick={() => this.handleActiveTimeDef(index)}
                            >
                                {value.active24 && '✓ '}{t('all_day')}
                            </button>
                        </div>
                    )}
                </div>
            ));

        let optionsCompany =
            companies?.results &&
            companies.results
                .filter((item) => item.id !== 1305 || item.id === null)
                .map((option) => {
                    const firstLetter = (option.name || '').charAt(0).toUpperCase();
                    return {
                        firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                        ...option,
                    };
                });

        if (
            this.state.pendingCreatedCompany &&
            !(optionsCompany || []).some((c) => c.id === this.state.pendingCreatedCompany.id)
        ) {
            optionsCompany = [
                ...(optionsCompany || []),
                this.state.pendingCreatedCompany,
            ];
        }

        // Check if offer is active on all days with active24
        let alwaysActive = true;

        this.state.torgid_offer_active_times.forEach((value) => {
            if (!value.active24 || !value.active) {
                alwaysActive = false;
            }
        });

        const baseActionType = toBaseActionType(this.state.action_type);
        const activationHelpText = ACTION_TYPES_WITH_HELP.includes(baseActionType)
            ? t('offer.activation_help_' + baseActionType)
            : null;

        return (
            <div className={styles.container}>
                <ConfirmDialog
                    open={!!this.state.dialogOpen}
                    title={t('missing_information')}
                    message={this.state.dialogMessage}
                    type="warning"
                    onConfirm={() => this.hideAlert()}
                    confirmText={t('ok')}
                />

                {/* TEXT CONTENT: IS and EN side by side */}
                <div style={{ marginBottom: 20 }} ref={(el) => (this.textSection = el)}>
                    <h4 className={styles.sectionTitle}>{t('offer.text')}</h4>
                    <div className={`${styles.sectionCard} ${this.state.titleState === 'error' || this.state.descriptionState === 'error' ? styles.sectionCardError : ''}`}>
                        {(() => {
                            const titleIsEmpty = !(this.state.title || '').trim();
                            const descIsEmpty = !(this.state.description || '').trim();
                            const titleEnFilled = !!(this.state.title_en || '').trim();
                            const descEnFilled = !!(this.state.description_en || '').trim();
                            const titleEnOnly = titleIsEmpty && titleEnFilled;
                            const descEnOnly = descIsEmpty && descEnFilled;
                            if (!titleEnOnly && !descEnOnly) return null;
                            const fields =
                                titleEnOnly && descEnOnly
                                    ? t('offer.text_en_only_title_and_description')
                                    : titleEnOnly
                                    ? t('offer.text_en_only_title')
                                    : t('offer.text_en_only_description');
                            return (
                                <div className={styles.enOnlyWarning} role="status">
                                    <span className={styles.enOnlyWarningIcon} aria-hidden>
                                        ⚠️
                                    </span>
                                    <span>
                                        {t('offer.text_en_only_warning', { fields })}
                                    </span>
                                </div>
                            );
                        })()}
                        <div className={styles.langGrid}>
                            {/* IS column */}
                            <div className={styles.langCol}>
                                <p className={styles.langColHeader}>🇮🇸 {t('icelandic')}</p>
                                <CustomInput
                                    formStyle={{ width: '100%' }}
                                    success={this.state.titleState === 'success'}
                                    error={this.state.titleState === 'error'}
                                    labelText={t('offer_title')}
                                    id="title"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        value: this.state.title,
                                        onChange: (event) =>
                                            this.change(event, 'title', 'length', 5),
                                    }}
                                />
                                <CustomInput
                                    success={this.state.descriptionState === 'success'}
                                    error={this.state.descriptionState === 'error'}
                                    inputProps={{
                                        minRows: 5,
                                        maxRows: 8,
                                        multiline: true,
                                        value: this.state.description,
                                        onChange: (event) =>
                                            this.change(event, 'description', 'length', 10),
                                    }}
                                    id="description"
                                    aria-label="description"
                                    labelText={t('offer_desc_is')}
                                    formStyle={{ width: '100%' }}
                                />
                            </div>

                            {/* Translate buttons column */}
                            <div className={styles.langDivider}>
                                <button
                                    className={styles.btn}
                                    onClick={() =>
                                        this.autoTranslate('title', 'title_en', 'is', 'en')
                                    }
                                    title={t('translate_is_en')}
                                >
                                    →
                                </button>
                                <button
                                    className={styles.btn}
                                    onClick={() =>
                                        this.autoTranslate(
                                            'description',
                                            'description_en',
                                            'is',
                                            'en'
                                        )
                                    }
                                    title={t('translate_is_en')}
                                >
                                    →
                                </button>
                            </div>

                            {/* EN column */}
                            <div className={styles.langCol}>
                                <p className={styles.langColHeader}>🇬🇧 {t('english')}</p>
                                <CustomInput
                                    formStyle={{ width: '100%' }}
                                    labelText={t('offer_title_en')}
                                    id="title_en"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        value: this.state.title_en,
                                        onChange: (event) => this.change(event, 'title_en'),
                                    }}
                                />
                                <CustomInput
                                    formStyle={{ width: '100%' }}
                                    id="description_en"
                                    aria-label="description_en"
                                    labelText={t('offer_desc_en')}
                                    inputProps={{
                                        minRows: 5,
                                        maxRows: 8,
                                        multiline: true,
                                        value: this.state.description_en,
                                        onChange: (event) => this.change(event, 'description_en'),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* TOP GRID: Images | Info */}
                <div className={styles.topGrid}>
                    {/* LEFT: Images */}
                    <div ref={(el) => (this.imagesSection = el)}>
                        <h4 className={styles.sectionTitle}>
                            {t('images')}
                            <HoverInfoBox>
                                {t('offer.images_help_size')}
                                <br style={{ marginBottom: 10 }} />
                                {t('offer.images_help_safe_area')}
                                <br style={{ marginBottom: 10 }} />
                                {t('offer.images_help_safe_area_note')}
                            </HoverInfoBox>
                        </h4>
                        <div className={`${styles.sectionCard} ${this.state.imagesState === 'error' ? styles.sectionCardError : ''}`}>
                            <div className={styles.imagesContainer}>
                                {isApiMockEnabled() && (
                                    <button
                                        type="button"
                                        data-testid="offer-e2e-seed-image"
                                        style={{
                                            marginBottom: 8,
                                            alignSelf: 'flex-start',
                                            fontSize: 12,
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => this.onUploadMainImage(E2E_TINY_PNG)}
                                    >
                                        E2E: setja mynd
                                    </button>
                                )}
                                <SingleImageUpload
                                    image={this.state.image}
                                    onUpload={(data) => this.onUploadMainImage(data)}
                                    onDelete={() => this.handleDeleteMainImage()}
                                    style={{ aspectRatio: '3/2', width: '100%' }}
                                    offer={previewOffer}
                                    overlay={
                                        <>
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    aspectRatio: '50/200',
                                                    background:
                                                        'repeating-linear-gradient(45deg,#007AAA, #007AAA 8px, white 8px, white 16px)',
                                                    opacity: 0.4,
                                                    height: '100%',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    aspectRatio: '50/200',
                                                    background:
                                                        'repeating-linear-gradient(315deg,#007AAA, #007AAA 8px, white 8px, white 16px)',
                                                    opacity: 0.4,
                                                    height: '100%',
                                                }}
                                            />
                                        </>
                                    }
                                />
                                <div className={styles.extraImagesGrid}>
                                    {this.state.images.map((item, index) => (
                                        <SingleImageUpload
                                            key={index}
                                            style={{ width: '100%' }}
                                            image={item.image}
                                            imgHolder={item.image || null}
                                            index={index}
                                            offer={previewOffer}
                                            id={item.id}
                                            onChangeOrder={(currentIndex, newIndex) =>
                                                this.changeImageOrder(
                                                    currentIndex - 1,
                                                    newIndex - 1
                                                )
                                            }
                                            onUpload={(data, i, id) =>
                                                this.onUpload(data, i - 1, id)
                                            }
                                            onDelete={this.deleteImageItem.bind(this)}
                                            onSetAsMain={this.handleSetAsMainImage}
                                            disabledDeleteBtn={
                                                this.state.images.length <= 1 && !this.state.image
                                            }
                                            errorStatus={this.state.imagesState}
                                        />
                                    ))}
                                    {this.state.image !== null &&
                                        this.state.image !== '' &&
                                        this.state.image !== undefined && (
                                            <SingleImageUpload
                                                style={{ width: '100%', aspectRatio: '3/2' }}
                                                offer={previewOffer}
                                                onUpload={(data, i, id) =>
                                                    this.onUpload(
                                                        data,
                                                        this.state.images.length,
                                                        id
                                                    )
                                                }
                                            />
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Info + Discount + Activation */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div ref={(el) => (this.infoSection = el)}>
                            <h4 className={styles.sectionTitle}>{t('offer.info')}</h4>
                            <div className={`${styles.sectionCard} ${this.state.companyState === 'error' ? styles.sectionCardError : ''}`}>
                                {/* Company selector */}
                                {(this.user_is_group_admin || this.isAdmin) && companies && (
                                    <div className={styles.companySelectRow}>
                                        <Autocomplete
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': { padding: '10px 14px' },
                                            }}
                                            variant="outlined"
                                            id="companies"
                                            onChange={(event, newValue) =>
                                                this.handleChangeCompanyGroup(newValue, 'companyId')
                                            }
                                            value={
                                                this.state.companyId
                                                    ? optionsCompany?.find?.(
                                                          (item) => item.id === this.state.companyId
                                                      ) || null
                                                    : null
                                            }
                                            options={
                                                optionsCompany
                                                    ? optionsCompany.sort?.(
                                                          (a, b) =>
                                                              -b.firstLetter.localeCompare(
                                                                  a.firstLetter
                                                              )
                                                      )
                                                    : []
                                            }
                                            groupBy={(option) => option.firstLetter}
                                            getOptionLabel={(option) => option?.name || ''}
                                            isOptionEqualToValue={(option, value) =>
                                                option?.id === value?.id
                                            }
                                            noOptionsText={
                                                this.canCreateCompany ? (
                                                    <div className={styles.noOptionsAddCompany}>
                                                        <span className={styles.noOptionsLabel}>
                                                            {t('offer.add_company_no_options')}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={styles.noOptionsAddButton}
                                                            onMouseDown={(event) => {
                                                                // Keep Autocomplete open until click is handled
                                                                event.preventDefault();
                                                            }}
                                                            onClick={() =>
                                                                this.setState({
                                                                    addCompanyModalOpen: true,
                                                                })
                                                            }
                                                        >
                                                            {t('offer.add_company')}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    t('offer.add_company_no_options')
                                                )
                                            }
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                    }}
                                                >
                                                    {option.logo ? (
                                                        <img
                                                            src={option.logo}
                                                            alt=""
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                objectFit: 'contain',
                                                                borderRadius: 4,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : (
                                                        <span
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 4,
                                                                background: 'var(--spara-surface-2, #eee)',
                                                                flexShrink: 0,
                                                                display: 'inline-block',
                                                            }}
                                                        />
                                                    )}
                                                    {option.name}
                                                </li>
                                            )}
                                            renderInput={(params) => {
                                                const selectedCompany = optionsCompany?.find(
                                                    (item) => item.id === this.state.companyId
                                                );
                                                return (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: selectedCompany?.logo ? (
                                                                <>
                                                                    <InputAdornment
                                                                        position="start"
                                                                        sx={{ ml: 1 }}
                                                                    >
                                                                        <img
                                                                            src={selectedCompany.logo}
                                                                            alt=""
                                                                            style={{
                                                                                width: 24,
                                                                                height: 24,
                                                                                objectFit: 'contain',
                                                                                borderRadius: 4,
                                                                            }}
                                                                        />
                                                                    </InputAdornment>
                                                                    {params.InputProps.startAdornment}
                                                                </>
                                                            ) : (
                                                                params.InputProps.startAdornment
                                                            ),
                                                        }}
                                                        sx={{
                                                            '& .MuiInputBase-root': { padding: '0' },
                                                            '& .MuiAutocomplete-input': {
                                                                marginTop: 0,
                                                                marginBottom: 0,
                                                                paddingLeft: selectedCompany?.logo
                                                                    ? 1
                                                                    : 10,
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                padding: '10px 14px',
                                                            },
                                                        }}
                                                        label={t('companies')}
                                                        error={this.state.companyState === 'error'}
                                                    />
                                                );
                                            }}
                                        />
                                        <AddCompanyFrom1819Modal
                                            open={this.state.addCompanyModalOpen}
                                            onClose={() =>
                                                this.setState({ addCompanyModalOpen: false })
                                            }
                                            onCreated={this.handleCompanyCreatedFrom1819}
                                        />
                                    </div>
                                )}
                                {!this.user_is_group_admin && this.state.companies.length > 1 && (
                                    <FormControl fullWidth style={{ marginBottom: 16 }}>
                                        <InputLabel htmlFor="simple-select">
                                            {t('companies')}
                                        </InputLabel>
                                        <Select
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '10px 14px',
                                                },
                                            }}
                                            value={this.state.companyId}
                                            onChange={(e) =>
                                                this.handleChangeCompany(
                                                    e.target.value,
                                                    'companyId'
                                                )
                                            }
                                            inputProps={{ name: 'companyId', id: 'companyId' }}
                                            renderValue={(selected) => {
                                                const company = (this.state.companies || []).find(
                                                    (c) => c.id === selected
                                                );
                                                if (!company) return '';
                                                return (
                                                    <span
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                        }}
                                                    >
                                                        {company.logo && (
                                                            <img
                                                                src={company.logo}
                                                                alt=""
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    objectFit: 'contain',
                                                                    borderRadius: 4,
                                                                }}
                                                            />
                                                        )}
                                                        {company.name}
                                                    </span>
                                                );
                                            }}
                                        >
                                            {this.state.companies
                                                .sort((a, b) =>
                                                    a.name > b.name ? 1 : b.name > a.name ? -1 : 0
                                                )
                                                .map((value, index) => (
                                                    <MenuItem key={index} value={value.id}>
                                                        <span
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 8,
                                                            }}
                                                        >
                                                            {value.logo && (
                                                                <img
                                                                    src={value.logo}
                                                                    alt=""
                                                                    style={{
                                                                        width: 24,
                                                                        height: 24,
                                                                        objectFit: 'contain',
                                                                        borderRadius: 4,
                                                                    }}
                                                                />
                                                            )}
                                                            {value.name}
                                                        </span>
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}

                                {/* Groups hint */}
                                {!this.props.skipStep2 && (
                                    <div
                                        style={{
                                            marginTop: 16,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '7px 10px',
                                            borderRadius: 6,
                                            backgroundColor: 'var(--spara-offer-well, #f0f7ff)',
                                            border: '1px solid var(--spara-border, #cce0ff)',
                                            color: 'var(--spara-offer-well-text, #1565c0)',
                                            fontSize: '0.82rem',
                                        }}
                                    >
                                        <span style={{ fontSize: '1rem' }}>👉</span>
                                        <span>{t('groups_moved_to_next_step')}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Discount type */}
                        <div ref={(el) => (this.discountSection = el)}>
                            <h4 className={styles.sectionTitle}>{t('offer.type')}</h4>
                            <div className={`${styles.sectionCard} ${this.state.discountState === 'error' ? styles.sectionCardError : ''}`}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <FormControl fullWidth error={!this.state.discount && this.state.discountState === 'error'}>
                                        <InputLabel
                                            htmlFor="simple-select"
                                            shrink={true}
                                            style={{ background: 'var(--spara-surface, white)' }}
                                        >
                                            {t('offer_new_discount')}
                                        </InputLabel>
                                        <Select
                                            data-testid="offer-discount-select"
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '10px 14px',
                                                },
                                            }}
                                            value={this.state.discount}
                                            onChange={(e) =>
                                                this.handleChange(e.target.value, 'discount')
                                            }
                                            inputProps={{
                                                name: 'discount',
                                                id: 'discount',
                                                'data-testid': 'offer-discount-input',
                                            }}
                                            label={t('offer_new_discount')}
                                        >
                                            <MenuItem
                                                classes={{ selected: classes.dSelected }}
                                                value="discount"
                                            >
                                                {t('offer_discount')}
                                            </MenuItem>
                                            <MenuItem
                                                classes={{ selected: classes.dSelected }}
                                                value="price"
                                            >
                                                {t('offer_price')}
                                            </MenuItem>
                                            <MenuItem
                                                classes={{ selected: classes.dSelected }}
                                                value="2 fyrir 1"
                                            >
                                                {t('offer_2_for_1')}
                                            </MenuItem>
                                            <MenuItem
                                                classes={{ selected: classes.dSelected }}
                                                value="sérkjör"
                                            >
                                                {t('offer_special')}
                                            </MenuItem>
                                            <MenuItem
                                                classes={{ selected: classes.dSelected }}
                                                value="tilkynning"
                                            >
                                                {t('offer_announcement')}
                                            </MenuItem>
                                            {(EVENTS_ENABLED || this.state.discount === 'skráning') && (
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="skráning"
                                                >
                                                    {t('offer_registration')}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    {(this.state.discount === 'discount' ||
                                        this.state.discount === 'price') && (
                                        <CustomInput
                                            labelText={<span>{t('offer_displayed_discount')}</span>}
                                            id="discount_input"
                                            formControlProps={{ fullWidth: true }}
                                            success={this.state.discountState === 'success'}
                                            error={!this.state.discountInput && this.state.discountState === 'error'}
                                            classes={{ select: classes.select }}
                                            inputProps={{
                                                value: this.state.discountInput,
                                                onChange: (event) =>
                                                    this.change(event, 'discountInput'),
                                                endAdornment: (
                                                    <InputAdornment
                                                        position="end"
                                                        className={classes.inputAdornment}
                                                    >
                                                        {this.state.discount === 'discount'
                                                            ? '%'
                                                            : 'kr.'}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Activation type */}
                        <div ref={(el) => (this.activationSection = el)}>
                            <h4 className={styles.sectionTitle}>
                                {t('offer.activation')}
                                <HoverInfoBox>{t('offer.activation_help')}</HoverInfoBox>
                            </h4>
                            <div className={styles.sectionCard}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <FlexContainer style={{ alignItems: 'center', gap: 8 }}>
                                        <FormControl fullWidth>
                                            <InputLabel
                                                htmlFor="action_type"
                                                shrink={true}
                                                style={{ background: 'var(--spara-surface, white)' }}
                                            >
                                                {t('activate_offer')}
                                            </InputLabel>
                                            <Select
                                                sx={{
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '10px 14px',
                                                    },
                                                }}
                                                label={t('activate_offer')}
                                                value={baseActionType}
                                                onChange={(e) =>
                                                    this.handleChange(e.target.value, 'action_type')
                                                }
                                                inputProps={{
                                                    name: 'action_type',
                                                    id: 'action_type',
                                                }}
                                            >
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="timer"
                                                >
                                                    {t('offer_timer')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="promocode"
                                                >
                                                    {t('offer_promocode')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="email"
                                                >
                                                    {t('offer_email')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="phone"
                                                >
                                                    {t('offer_phone')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="website"
                                                >
                                                    {t('offer_website')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="barcode"
                                                >
                                                    {t('offer_barcode')}
                                                </MenuItem>
                                                <MenuItem
                                                    classes={{ selected: classes.dSelected }}
                                                    value="qrcode"
                                                >
                                                    {t('offer_qr_code')}
                                                </MenuItem>
                                                {SHOW_MEMBERSHIP_CARD_ACTIVATION && (
                                                    <MenuItem
                                                        classes={{ selected: classes.dSelected }}
                                                        value="membership_card"
                                                    >
                                                        {t('offer_membership_card')}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                        {activationHelpText && (
                                            <HoverInfoBox>{activationHelpText}</HoverInfoBox>
                                        )}
                                    </FlexContainer>
                                    {SHOW_LIMITED_ACTIVATION_OPTIONS &&
                                        LIMITED_ACTION_TYPE_MAP[baseActionType] && (
                                        <ToggleButtonGroup
                                            exclusive
                                            fullWidth
                                            size="small"
                                            value={
                                                isLimitedActionType(this.state.action_type)
                                                    ? 'einnota'
                                                    : 'fjolnota'
                                            }
                                            onChange={(e, value) =>
                                                this.handleActivationModeChange(value)
                                            }
                                            sx={{
                                                '& .MuiToggleButton-root': {
                                                    color: 'var(--spara-text)',
                                                    borderColor:
                                                        'var(--spara-border) !important',
                                                    backgroundColor: 'var(--spara-surface-2)',
                                                },
                                                '& .MuiToggleButton-root.Mui-selected': {
                                                    backgroundColor:
                                                        'var(--spara-primary) !important',
                                                    color: '#fff !important',
                                                    borderColor:
                                                        'var(--spara-primary) !important',
                                                    fontWeight: 700,
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'var(--spara-primary) !important',
                                                        filter: 'brightness(1.08)',
                                                    },
                                                },
                                            }}
                                        >
                                            <ToggleButton
                                                value="fjolnota"
                                                data-testid="offer-activation-mode-multi"
                                            >
                                                {t('offerActivationTypeMulti')}
                                            </ToggleButton>
                                            <ToggleButton
                                                value="einnota"
                                                data-testid="offer-activation-mode-single"
                                            >
                                                {t('offerActivationTypeLimited')}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    )}
                                    {SHOW_LIMITED_ACTIVATION_OPTIONS &&
                                        isLimitedActionType(this.state.action_type) && (
                                        <FormControl
                                            fullWidth
                                            error={this.state.activationFrequencyState === 'error'}
                                        >
                                            <FormLabel
                                                error={
                                                    this.state.activationFrequencyState === 'error'
                                                }
                                                style={{ marginBottom: 8 }}
                                            >
                                                {t('offerActivationFrequencyLabel')}
                                            </FormLabel>
                                            <ToggleButtonGroup
                                                exclusive
                                                size="small"
                                                data-testid="offer-activation-frequency-select"
                                                value={this.state.activationFrequencyPreset || null}
                                                onChange={(e, value) => {
                                                    if (value === null) return;
                                                    this.handleActivationFrequencyPresetChange(value);
                                                }}
                                                sx={{
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                    '& .MuiToggleButtonGroup-grouped': {
                                                        border:
                                                            '1px solid var(--spara-border) !important',
                                                        borderRadius: '999px !important',
                                                        margin: 0,
                                                        padding: '4px 16px',
                                                        textTransform: 'none',
                                                        color: 'var(--spara-text)',
                                                        backgroundColor: 'var(--spara-surface-2)',
                                                    },
                                                    '& .MuiToggleButton-root.Mui-selected': {
                                                        backgroundColor:
                                                            'var(--spara-primary) !important',
                                                        color: '#fff !important',
                                                        borderColor:
                                                            'var(--spara-primary) !important',
                                                        fontWeight: 700,
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'var(--spara-primary) !important',
                                                            filter: 'brightness(1.08)',
                                                        },
                                                    },
                                                }}
                                            >
                                                <ToggleButton value="once">
                                                    {t('offerActivationFrequencyOnce')}
                                                </ToggleButton>
                                                <ToggleButton value="week">
                                                    {t('offerActivationFrequencyWeek')}
                                                </ToggleButton>
                                                <ToggleButton value="month">
                                                    {t('offerActivationFrequencyMonth')}
                                                </ToggleButton>
                                                <ToggleButton value="quarter">
                                                    {t('offerActivationFrequencyQuarter')}
                                                </ToggleButton>
                                                <ToggleButton value="year">
                                                    {t('offerActivationFrequencyYear')}
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </FormControl>
                                    )}
                                    {(this.state.action_value || this.state.action_type) &&
                                        !LIMITED_ACTION_TYPE_MAP[baseActionType] && (
                                            <CustomInput
                                                labelText={
                                                    <span>
                                                        {ACTION_TYPES_WITH_VALUE.includes(
                                                            this.state.action_type
                                                        )
                                                            ? t(
                                                                  'offer_active_' +
                                                                      this.state.action_type
                                                              )
                                                            : t('value')}
                                                    </span>
                                                }
                                                id="action_value"
                                                formControlProps={{ fullWidth: true }}
                                                inputProps={{
                                                    value: this.state.action_value,
                                                    onChange: (event) =>
                                                        this.change(event, 'action_value'),
                                                }}
                                            />
                                        )}
                                    {this.state.action_type === 'promocode' && (
                                        <CustomInput
                                            labelText={<span>{t('offer_link')}</span>}
                                            id="offer_link"
                                            formControlProps={{ fullWidth: true }}
                                            inputProps={{
                                                value: this.state.offer_link,
                                                onChange: (event) =>
                                                    this.change(event, 'offer_link'),
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end right column wrapper */}
                </div>
                {/* end topGrid */}

                {/* When active */}
                <div style={{ marginTop: 20 }}>
                    <h4 className={styles.sectionTitle}>{t('offer.active_when')}</h4>
                    <div className={styles.sectionCard}>
                        <div className={styles.whenActiveGrid}>
                            {/* LEFT: Tímabil */}
                            <div>
                                <p className={styles.whenActiveColHeader}>{t('offer.period')}</p>
                                <p className={styles.whenActiveColDesc}>
                                    {t('offer.period_desc')}
                                </p>

                                <div className={styles.activeDaysList}>
                                    <div className={styles.dayRow}>
                                        <span className={styles.dateRowLabel}>
                                            {t('offer.period_starts')}
                                        </span>
                                        <div className={styles.dateInputWrap}>
                                            <Datetime
                                                key={this.state.start_date || 'no-start'}
                                                value={
                                                    this.state.start_date
                                                        ? moment(this.state.start_date)
                                                        : ''
                                                }
                                                onChange={(e) =>
                                                    this.handleChange(
                                                        e && e._isAMomentObject
                                                            ? e.format('YYYY-MM-DD')
                                                            : e,
                                                        'start_date'
                                                    )
                                                }
                                                timeFormat={false}
                                                closeOnSelect={true}
                                                inputProps={{
                                                    placeholder: t(
                                                        'offer.period_starts_placeholder'
                                                    ),
                                                    className: styles.datePickerInput,
                                                }}
                                                dateFormat="ddd, D. MMMM YYYY"
                                                locale={i18n.language === 'en' ? 'en' : 'is'}
                                            />
                                            {this.state.start_date && (
                                                <button
                                                    type="button"
                                                    className={styles.dateClearBtn}
                                                    onClick={() =>
                                                        this.handleChange('', 'start_date')
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.dayRow}>
                                        <span className={styles.dateRowLabel}>
                                            {t('offer.period_ends')}
                                        </span>
                                        <div className={styles.dateInputWrap}>
                                            <Datetime
                                                key={this.state.end_date || 'no-end'}
                                                value={
                                                    this.state.end_date
                                                        ? moment(this.state.end_date)
                                                        : ''
                                                }
                                                onChange={(e) =>
                                                    this.handleChange(
                                                        e && e._isAMomentObject
                                                            ? e.format('YYYY-MM-DD')
                                                            : e,
                                                        'end_date'
                                                    )
                                                }
                                                timeFormat={false}
                                                closeOnSelect={true}
                                                inputProps={{
                                                    placeholder: t(
                                                        'offer.period_ends_placeholder'
                                                    ),
                                                    className: styles.datePickerInput,
                                                }}
                                                dateFormat="ddd, D. MMMM YYYY"
                                                locale={i18n.language === 'en' ? 'en' : 'is'}
                                            />
                                            {this.state.end_date && (
                                                <button
                                                    type="button"
                                                    className={styles.dateClearBtn}
                                                    onClick={() =>
                                                        this.handleChange('', 'end_date')
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: Vikuvirkjun */}
                            <div>
                                <p className={styles.whenActiveColHeader}>{t('offer.weekly_activation')}</p>
                                <p className={styles.whenActiveColDesc}>
                                    {t('offer.weekly_activation_desc')}
                                </p>
                                <div className={styles.activeTimesHeader}>
                                    <button
                                        type="button"
                                        onClick={() => this.handleSelectAllActiveTimes()}
                                        className={`${styles.allDayBtn} ${
                                            alwaysActive ? styles.allDayBtnActive : ''
                                        }`}
                                    >
                                        {alwaysActive && '✓ '}{t('all_days_of_week')}
                                    </button>
                                </div>
                                <div className={styles.activeDaysList}>{renderActiveTime}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export const HourInput = ({ value, onChange, ...props }) => {
    return (
        <input
            id="name"
            style={{
                padding: '5px 4px',
                width: 60,
                textAlign: 'center',
                border: '1px solid var(--spara-input-border, #ddd)',
                borderRadius: 6,
                fontSize: '0.82rem',
                fontWeight: 600,
                color: 'var(--spara-text-strong, #333)',
                background: 'var(--spara-input-bg, #fafafa)',
                outline: 'none',
            }}
            value={value}
            onChange={(event) => {
                onChange?.(event.target.value);
            }}
            onFocus={(event) => {
                event.target.select();
            }}
            onBlur={(event) => {
                onChange?.(getTime(event.target.value));
            }}
            {...props}
        />
    );
};

const getTime = (textValue) => {
    const value = parseInt(textValue);
    if (isNaN(value)) {
        return textValue;
    }

    let newValue = '';

    if (value >= 0 && value <= 24 && !textValue.includes(':')) {
        newValue = (value < 10 ? '0' : '') + value.toString() + ':00';
    }

    if (textValue.length == 3) {
        newValue = '0' + textValue.substring(0, 1) + ':' + textValue.substring(1, 3);
    }

    if (textValue.length == 4) {
        newValue = textValue.substring(0, 2) + ':' + textValue.substring(2, 4);
    }
    if (newValue == '') {
        return textValue;
    }
    newValue = newValue.substring(0, 5);
    return newValue;
};

export default withTranslation('', { withRef: true })(Step1);
